import axios from "axios"
import BaseApiService from "@/api/BaseApiService"

/**
 * Why an exchange did not produce a token.
 *
 * Only "invalid" means the token itself was no good. The other two are
 * transport problems, and the caller must not treat them as a rejected login:
 * swallowing a rate limit as "token invalid" is what kept the limit of ten
 * requests per minute unnoticed for months.
 */
export type AuthTokenFailure = "invalid" | "rate-limited" | "unavailable"

export interface AuthTokenResult {
  success: boolean
  token?: string
  reason?: AuthTokenFailure
}

export default class AuthService extends BaseApiService {
  /**
   * Exchanges the one-time token of a module entry for the API token.
   *
   * The user is no longer sent along: the backend reads it from the stored
   * token, so naming it here would have decided nothing.
   *
   * @param oneTimeToken Token taken from the entry URL
   */
  getUserToken(oneTimeToken: string): Promise<AuthTokenResult> {
    return axios
      .get(this.getApiUrl("/auth/check-token"), { params: { ott: oneTimeToken } })
      .then((res) => {
        if (res.data?.success && res.data?.token) {
          return { success: true, token: res.data.token }
        }

        return { success: false, reason: "invalid" as AuthTokenFailure }
      })
      .catch((error) => {
        // An answer with status 429 is the rate limiter; any other error status
        // and a missing answer alike mean we never learned whether the token
        // was good, which is not the same as it being bad.
        const reason: AuthTokenFailure = error?.response?.status === 429 ? "rate-limited" : "unavailable"

        return { success: false, reason: reason }
      })
  }
}
